import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect, useContext } from 'react';
import { colors, layout, shadows } from '../../theme';
import Fetch from '../../utils/Fetch';
import DownloadService from '../../utils/download-service';
import Helper from '../../utils/helper';
import * as Enums from '../../utils/enums';
import InvoiceDetail from '../../components/customer-zone/invoice/invoice-detail';
import InvoiceItems from '../../components/customer-zone/invoice/invoice-items';
import InvoiceTotals from '../../components/customer-zone/invoice/invoice-totals';
import MessagePost from '../../components/modals/message-post';
import ToastContext from '../../utils/toast-context';
import ConfirmAction from '../../components/modals/confirm-action';
import PageContext from '../../utils/page-context';
import NoSSR from '../../utils/no-ssr'
import useMobileView from '../../hooks/useMobileView';
import PaymentService from '../../services/payment-service';
import { roundPrice } from '../../utils/line-totals';
import CompanyService from '../../services/company-service';
import LeadMessage from '../../components/customer-zone/general/lead-message';
import {getCustomerZoneItemPayments} from "../../PageComponents/Payments/payments";
import {useQuery} from "@tanstack/react-query";
import PaymentsTable from "../../PageComponents/CustomerZone/PaymentsTable";
import { useRouter } from 'next/router';
import { Flex, Button as MantineButton, Menu } from "@mantine/core";
import { IconCaretDownFilled, IconBrandWhatsapp } from '@tabler/icons-react';

function ViewInvoice() {

  const router = useRouter();
  const { t, c, i, s, com } = router.query as any;

  const toast = useContext<any>(ToastContext);

  const pageContext = useContext<any>(PageContext);

  const [mobileView] = useMobileView();

  const tenantQuery = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t } as any),
    { enabled: !!t, onError: console.error }
  );

  const tenant = (tenantQuery.data?.HttpStatusCode === 200 && tenantQuery.data?.Results?.length > 0)
    ? tenantQuery.data.Results[0]
    : null;

  const redirect = (tenantQuery.isSuccess && !tenant)
    ? `/customerzone/invoicelist?t=${t}&c=${c}`
    : null;

  const invoiceQuery = useQuery(
    ['czInvoice', t, c, i],
    () => Fetch.get({
      url: `/CustomerZone/GetInvoice`,
      params: { invoiceID: i },
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    }),
    { enabled: !!tenant, onError: console.error }
  );

  const companyQuery = useQuery(
    ['czCompany', t, c],
    () => CompanyService.getCustomerZoneCompany(t, c, tenant.API),
    { enabled: !!tenant, onError: console.error }
  );

  const [invoice, setInvoice] = useState<any>(null);

  useEffect(() => {
    if (invoiceQuery.data) {
      setInvoice(invoiceQuery.data);
    }
  }, [invoiceQuery.data]);

  useEffect(() => {
    if (redirect) {
      Helper.nextRouter(router.replace, redirect);
    }
  }, [redirect]);

  useEffect(() => {
    if (tenant && invoiceQuery.data) {
      pageContext.updateTenantName(invoiceQuery.data.headerTenantName);
    }
  }, [tenant, invoiceQuery.data]);

  const command = com;
  const tenantID = t;
  const customerID = c;
  const apiUrl = tenant?.API;

  const company = companyQuery.data;
  const currencySymbol = company?.Currency?.Symbol || '';
  const taxPercentage = company?.TaxPercentage;

  const store = invoiceQuery.data?.Store || null;
  const bankName = store ? (store.BankName || null) : (company?.BankName || null);
  const bankAccountNumber = store ? (store.BankAccountNumber || null) : (company?.BankAccountNumber || null);
  const bankBranchCode = store ? (store.BankBranchCode || null) : (company?.BankBranchCode || null);

  const primaryContact = (invoice?.Contact ? invoice.Contact : undefined);
  const invoiceLocation = (invoice?.Location ? invoice.Location : undefined);

  const itemPaymentsQuery = useQuery(['payments', 'customerZoneItemList', invoice?.ID],
      () => getCustomerZoneItemPayments(invoice.ID, Enums.Module.Invoice, tenantID, customerID, apiUrl), {
        enabled: !!invoice,
        onError: console.error
      });

  const exportDocument = () => {
    DownloadService.downloadFile("GET", `/CustomerZone/GetInvoiceDocument?invoiceID=${invoice.ID}`, null, true, false,
      tenantID, customerID, tenant.API, mobileView);
  };

  const downloadDocument = () => {
    DownloadService.downloadFile("GET", `/CustomerZone/GetInvoiceDocument?invoiceID=${invoice.ID}`, null, false, false,
      tenantID, customerID, tenant.API, mobileView);
  };

  const handleWhatsAppClick = () => {
    if (!company?.WhatsAppNumber) return;

    const message = `Hi! This is ${invoice.CustomerName} reaching out about invoice ${invoice.InvoiceNumber}.`;
    let phone = company.WhatsAppNumber.replace(/\D/g, '');
    if (phone.startsWith('0')) {
      phone = '27' + phone.substring(1);
    }

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const [showMessagePost, setShowMessagePost] = useState(false);
  const onMessagePost = (state) => {
    setShowMessagePost(false);
  };

  // TODO: InvoiceStatus.Accepted and InvoiceStatus.Declined don't exist in utils/enums.js yet.
  // Commented out until those values are added to the enum and this is wired up to a button.
  // const acceptDeclineInvoiceClick = (status) => {
  //   if (status == Enums.InvoiceStatus.Accepted) {
  //     confirmChangeStatus('accept', Enums.InvoiceStatus.Accepted);
  //   } else if (status == Enums.InvoiceStatus.Declined) {
  //     confirmChangeStatus('decline', Enums.InvoiceStatus.Declined);
  //   }
  // };

  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const confirmChangeStatus = async (statusString, status) => {
    setConfirmOptions({
      display: true,
      heading: "Confirm",
      text: `Are you sure you want to ${statusString} this invoice?`,
      confirmButtonText: "Confirm",
      showCancel: true,
      onConfirm: async () => {
        updateInvoiceStatus(status);
      }
    } as any);
  };

  const updateInvoiceStatus = async (status) => {
    const put = await Fetch.put({
      url: `/CustomerZone/UpdateInvoiceStatus?id=${invoice.ID}&invoiceStatus=${status}`,
      tenantID: tenantID,
      customerID: customerID,
      apiUrlOverride: tenant.API
    });
    if (put.ID) {
      // console.log(put);
      setInvoice(put);
      toast.setToast({
        message: 'Status updated successfully',
        show: true,
        type: 'success'
      });
    } else {
      toast.setToast({
        message: 'Error updating status',
        show: true,
        type: Enums.ToastType.error,
      });
    }
  };

  const [payNowLink, setPayNowLink] = useState(null);

  const getPayNowLink = async () => {
    if (!!redirect || !tenant || !invoice) return;

    const payments = itemPaymentsQuery.data?.Results || [];
    const totalPaid = payments.reduce((sum: number, p: any) => sum + +p.Amount, 0);
    const amountDue = Math.max(0, roundPrice(invoice.TotalInclusive - totalPaid));

    let url = await PaymentService.getPayNowLink(invoice.ID, invoice.InvoiceNumber, Enums.Module.Invoice, amountDue,
      tenantID, customerID, tenant.API);

    setPayNowLink(url);
  };

  useEffect(() => {
    getPayNowLink();
  }, [invoice, tenant, itemPaymentsQuery.data]);

  const payNow = () => {
    if (!!payNowLink) window.location.href = payNowLink;
  }

  useEffect(() => {
    if (payNowLink && command == "paynow" && (invoice?.InvoiceStatus === Enums.InvoiceStatus.Unpaid || invoice?.InvoiceStatus === Enums.InvoiceStatus.Overdue)) {
      payNow();
    }
  }, [payNowLink]);

  const displayInvoiceButtons = () => {
    return (<div className={"invoice-buttons"}>

      {(invoice.InvoiceStatus === Enums.InvoiceStatus.Unpaid || invoice.InvoiceStatus === Enums.InvoiceStatus.Overdue) && !!payNowLink ?
        <div className={mobileView ? "row" : ""}>
          <div className={mobileView ? "center" : "btn-container"}>
            <img src="/Red-Large-Square-PayNow.png" className="pay-now" onClick={() => payNow()} />
          </div>
        </div> : ""
      }

      <style jsx>{`

        .pay-now {
          margin-top: 0.5rem;
          height: 46px;
          cursor: pointer;
        }

        .invoice-buttons {
          margin-bottom: 0.5rem;
          ${mobileView ? "width: 100%;" : ""}
        }

        .btn-container {
          display: inline-block;
          margin-left: 0.5rem;
        }

        .row {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
        .column {
          display: flex;
          flex-basis: 0;
          flex-direction: column;
          flex-grow: 1;
        }

        .left {
          position: relative;
          margin-left: 0.5rem;
          width: calc(50vw - 0.75rem);
        }
        .right {
          position: relative;
          margin-right: 0.5rem;
          width: calc(50vw - 0.75rem);
        }
        .center {
          width: 100%;
          text-align: center;
        }
        .center .pay-now {
          margin-bottom: -0.5rem;
        }

      `}</style>
    </div>);
  }

  if (redirect) {
    return (<>You are not authorised, redirecting you back to home</>);
  }

  if (!tenant || !invoice || invoiceQuery.isLoading || companyQuery.isLoading) {
    return (<>Loading...</>);
  }

  if (command === "paynow") {
    return <>
      Redirecting...
    </>
  }

  return (
      <NoSSR>
        <LeadMessage customerID={customerID} tenantID={tenantID} page={"customerzone/viewinvoice"}/>

        <div style={{
          paddingInlineEnd: 'var(--mantine-spacing-sm)',
          minHeight: 'calc(100dvh - 20px)'
        }}>
          <Flex>
            <Flex>
              <h3>{invoice.InvoiceNumber}</h3>
            </Flex>
            <Flex ml={'auto'}>
              <Flex ml={'auto'} wrap={'wrap-reverse'}>

                <Flex ml={'auto'}>
                  {!mobileView ? displayInvoiceButtons() : ""}
                </Flex>

                <Flex ml={'auto'}>
                  {company?.WhatsAppNumber && (
                      <MantineButton
                          bg={'white'}
                          variant="outline"
                          color="#25D366"
                          leftSection={<IconBrandWhatsapp size={16} />}
                          onClick={handleWhatsAppClick}
                          name="wa-cz-invoice-followup"
                          h={40}
                          mt={8}
                          mr={8}
                          styles={{
                            root: {
                              fontWeight: 500,
                              flexShrink: 0
                            }
                          }}
                      >
                        Follow up on WhatsApp
                      </MantineButton>
                  )}

                  <div title="Submit question or comment" className="message-button-container"
                       onClick={() => setShowMessagePost(true)}>
                    <img src={Enums.Icon.MessageCircle} alt="icon" className="icon"/>
                  </div>
                  {showMessagePost ?
                      <MessagePost
                          onMessagePost={onMessagePost}
                          module={Enums.Module.Invoice}
                          itemID={invoice.ID}
                          customerZone={true}
                          tenantID={tenantID}
                          customerID={customerID}
                          apiUrlOverride={tenant.API}
                      /> : ''
                  }
                </Flex>

                <MantineButton.Group ml={'auto'} style={{ marginTop: '0.5rem' }}>
                  <MantineButton h={40} onClick={exportDocument}>Print</MantineButton>
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <MantineButton h={40} miw={50} w={50} style={{ borderLeft: '1px solid rgba(255,255,255,0.3)' }}>
                        <IconCaretDownFilled size={12} />
                      </MantineButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={downloadDocument}>Download</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </MantineButton.Group>
              </Flex>
            </Flex>
          </Flex>
          <div className="row">
            <div className="contact">
              {primaryContact ?
                  <>
                    <p className="contact-name">{primaryContact.FirstName + " " + primaryContact.LastName}</p>
                    <p>{invoice.CustomerName}</p>
                    <p>{primaryContact.MobileNumber}</p>
                    <p>{primaryContact.EmailAddress}</p>
                  </>
                  : ""}
              <p>{invoiceLocation ? invoiceLocation.LocationDisplay : "No Location"}</p>
            </div>
          </div>

          <div className="card-container">
            <InvoiceDetail invoice={invoice} showPrint={false} tenantID={tenantID} customerID={customerID}
                           showTotals={false} showHyperlink={false}
                           apiUrlOverride={tenant.API} mobileView={mobileView}/>
          </div>
          <div className="card-container">
            <InvoiceItems invoiceItems={invoice.InvoiceItems} mobileView={mobileView}/>
          </div>

          <Flex
              justify={'end'}
              wrap={'wrap'}
              gap={'.5em'}
              align={'flex-start'}
          >
            {(bankName || bankAccountNumber || bankBranchCode) && (
              <div style={{marginRight: 'auto', marginTop: '0.5rem'}}>
                <p style={{margin: '3px 0 0'}}><strong>Banking Details</strong></p>
                {bankName && <p style={{margin: '3px 0 0'}}>Bank: {bankName}</p>}
                {bankAccountNumber && <p style={{margin: '3px 0 0'}}>Account Number: {bankAccountNumber}</p>}
                {bankBranchCode && <p style={{margin: '3px 0 0'}}>Branch Code: {bankBranchCode}</p>}
              </div>
            )}
            {
                itemPaymentsQuery.data?.Results && itemPaymentsQuery.data?.Results.length > 0 &&
                <div className="card-container" style={{flex: 2}}>
                  <div className="card" style={{padding: '.5rem'}}>
                    <h2 className="item-heading">Payments</h2>
                    <PaymentsTable paymentList={itemPaymentsQuery.data?.Results || []}/>
                  </div>
                </div>
            }
            <div className="card-container">
              {mobileView ?
                  <InvoiceTotals invoice={invoice} mobileView={mobileView} currencySymbol={currencySymbol}
                                 paymentList={itemPaymentsQuery.data?.Results || []} taxPercentage={taxPercentage}/> : <div className="row">
                    <div className="column"></div>
                    <div className="column-end">
                      <InvoiceTotals invoice={invoice} mobileView={mobileView} currencySymbol={currencySymbol}
                                     paymentList={itemPaymentsQuery.data?.Results || []} taxPercentage={taxPercentage}/>
                    </div>
                  </div>}
            </div>
          </Flex>

        </div>


        {mobileView ? <div className="row spacer">
          <div className="floating-action-button row">
            {displayInvoiceButtons()}
          </div>
        </div> : ""}

        <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions}/>

        <style jsx>{`

            .item-heading {
              margin: 0 0 1rem 0;
              padding: 0;
              font-size: 1.1rem;
              width: 120px;
            }

            .card {
              background-color: ${colors.white};
              border-radius: ${layout.cardRadius};
              box-shadow: ${shadows.card};
              box-sizing: border-box;
              padding: 1rem 2rem;
              position: relative;
              width: 100%;
            }

            .card-container {
              margin-top: 0.5rem;
            }


            .card h3 {
              margin-top: 0;
            }

            .container {
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              padding: 1.5rem 3rem;
              overflow-x: hidden;
            }

            .row {
              display: flex;
            }

            .column {
              display: flex;
              flex-basis: 0;
              flex-direction: column;
              flex-grow: 1;
            }

            .column :global(.textarea-container) {
              height: 100%;
            }

            .column + .column {
              margin-left: 1.25rem;
            }

            .contact {
              width: 100%;
              padding-right: 16px;
            }

            .contact p {
              margin: 3px 0 0;
            }

            .contact p.contact-name {
              font-weight: bold;
            }

            .heading {
              color: ${colors.blueGrey};
              font-weight: bold;
            }

            .new-comment {
              position: relative;
            }

            .new-comment :global(.textarea-container) {
              height: 5rem;
            }

            .new-comment img {
              cursor: pointer;
              position: absolute;
              right: 1rem;
              top: 1rem;
            }

            .loader {
              border-color: rgba(113, 143, 162, 0.2);
              border-left-color: ${colors.blueGrey};
              display: block;
              margin-bottom: 1rem;
              margin-top: 1rem;
            }

            .comment {
              background-color: ${colors.white};
              border-radius: ${layout.cardRadius};
              box-sizing: border-box;
              color: ${colors.blueGrey};
              display: flex;
              flex-direction: column;
              justify-content: center;
              margin-top: 0.5rem;
              padding: 1.25rem 1rem;
              position: relative;
              width: 100%;
            }

            .comment-info {
              align-items: center;
              display: flex;
              margin-bottom: 4px;
            }

            .job {
              color: ${colors.bluePrimary};
              font-weight: bold;
            }

            .name {
              color: ${colors.darkPrimary};
              font-weight: bold;
            }

            .time {
              color: ${colors.blueGrey};
              font-size: 12px;
              margin-left: 1rem;
            }

            .text {
              white-space: pre-wrap;
            }

            .edit {
              margin-top: 0.2rem;
              margin-left: 1rem;
            }

            .status {
              position: absolute;
              right: 0;
              top: 0;
              width: 20rem;
            }

            .status :global(.input-container) {
              background-color: ${colors.bluePrimary};
            }

            .status :global(input) {
              color: ${colors.white};
            }

            .status :global(label) {
              color: ${colors.white};
              opacity: 0.8;
            }

            .actions {
              display: flex;
              flex-direction: row;
            }

            .action-button {
              margin-top: -1rem;
              margin-left: 0.5rem;
            }

            .message-button-container {
              align-items: center;
              border: 1px solid ${colors.blueGreyLight};
              border-radius: ${layout.cardRadius};
              box-sizing: border-box;
              cursor: pointer;
              display: flex;
              font-weight: bold;
              height: 2.5rem;
              justify-content: space-between;
              margin-top: 0.5rem;
              padding: 0.5rem 0.5rem 0.5rem 0.5rem;
              position: relative;
              margin-right: 0.5rem;
              margin-left: 0.5rem;
              background: ${colors.white};
            }

            .button-dropdown {
              width: 80px;
            }

            .floating-action-button {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              background: ${colors.white};
              box-shadow: ${shadows.cardSmallDark};
            }

          `}</style>
      </NoSSR>
);
}

export default ViewInvoice;

export const Route = createFileRoute('/customerzone/viewinvoice')({ component: ViewInvoice })
